import React, { useEffect, useMemo, useState } from "react";
import { FileText, AlertCircle, CheckCircle } from "lucide-react";
import { apiConfig } from "../config/api.config";

const COLORS = {
  danger: "#ef4444",
  warning: "#f59e0b",
  success: "#10b981",
  info: "#3b82f6",
  purple: "#8b5cf6",
  gray: "#6b7280",
};

function toNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function clamp0(n) {
  const x = Number(n);
  return Number.isFinite(x) ? Math.max(0, x) : 0;
}

// ✅ Porcentaje seguro (0..100+), evita NaN/Infinity
function pct(part, total) {
  const p = toNum(part);
  const t = toNum(total);
  if (t <= 0) return 0;
  return (p / t) * 100;
}

// ✅ Formato % consistente
function fmtPct(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "0.0%";
  return `${n.toFixed(1)}%`;
}

const Presupuesto = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  // ✅ Tiempo real (polling + al volver a la pestaña)
  useEffect(() => {
    let alive = true;
    let timer = null;

    const tick = async () => {
      if (!alive) return;
      await cargarDatos();
      if (!alive) return;
      timer = setTimeout(tick, 10 * 60 * 1000); // 10 minutos
    };

    tick();

    const onVis = () => {
      if (document.visibilityState === "visible") cargarDatos();
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const role = localStorage.getItem("role") || "usuario";
      const cacheBust = `t=${Date.now()}`;

      const url = apiConfig.getUrl(
        "/api/presupuesto.php",
        `role=${encodeURIComponent(role)}&${cacheBust}`
      );

      const resp = await fetch(url, { credentials: "include" });

      // Evita el error de JSON cuando el server devuelve HTML
      const raw = await resp.text();
      let parsed = null;
      try {
        parsed = raw ? JSON.parse(raw) : null;
      } catch {
        console.error(
          "Respuesta NO JSON desde /api/presupuesto.php:",
          raw.slice(0, 300)
        );
        throw new Error("La API no devolvió JSON.");
      }

      if (parsed?.ok) setData(parsed.data);
      else setData(null);
    } catch (e) {
      console.error("Error cargando presupuesto:", e);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const computed = useMemo(() => {
    const necAprob = toNum(data?.necesidades_aprobadas);

    // CDP (por solicitud)
    const cdpSolic = toNum(data?.cdp_solicitados);
    const cdpAprob = toNum(
      data?.cdp_aprobados_solicitudes ?? data?.cdp_aprobados
    );
    const cdpRech = toNum(data?.cdp_rechazados);
    const cdpEnProc = clamp0(cdpSolic - cdpAprob - cdpRech);

    // RP (por solicitud)
    const rpSolic = toNum(data?.rp_solicitados);
    const rpAprob = toNum(data?.rp_aprobados_solicitudes ?? data?.rp_aprobados);
    const rpRech = toNum(data?.rp_rechazados);
    const rpEnProc = clamp0(rpSolic - rpAprob - rpRech);

    // RP: Sin solicitud = Total CDP aprobados - Solicitudes de RP
    const rpSinSolicitud = clamp0(cdpAprob - rpSolic);

    // Necesidades sin RP = Total necesidades - RP aprobados
    const necesidadesSinRP = clamp0(necAprob - rpAprob);

    // Secretarías (alfabético) + TOTAL arriba
    const distRaw = Array.isArray(data?.distribucion_secretarias)
      ? data.distribucion_secretarias
      : [];
    const distSorted = [...distRaw].sort((a, b) =>
      String(a?.secretaria ?? "").localeCompare(
        String(b?.secretaria ?? ""),
        "es",
        { sensitivity: "base" }
      )
    );

    const tot = data?.totalizador || {};
    const totalRow = {
      secretaria: "TOTAL",
      necesidades_aprobadas: toNum(tot.necesidades_aprobadas),
      cdp_aprobados: toNum(tot.cdp_aprobados),
      rp_aprobados: toNum(tot.rp_aprobados),
      __isTotal: true,
    };

    const distWithTotalFirst = [totalRow, ...distSorted];

    return {
      necAprob,
      cdp: {
        solicitados: cdpSolic,
        aprobados: cdpAprob,
        rechazados: cdpRech,
        enProceso: cdpEnProc,
      },
      rp: {
        solicitados: rpSolic,
        aprobados: rpAprob,
        sinSolicitud: rpSinSolicitud,
        rechazados: rpRech,
        enProceso: rpEnProc,
      },
      necesidadesSinRP,
      distWithTotalFirst,
    };
  }, [data]);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "400px",
          padding: "2rem",
        }}
      >
        <div
          style={{
            width: "48px",
            height: "48px",
            border: "4px solid #e5e7eb",
            borderTop: "4px solid #3b82f6",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ textAlign: "center", padding: "4rem 2rem", color: "#6b7280" }}>
        No se pudieron cargar los datos presupuestales
      </div>
    );
  }

  return (
    <div
      style={{
        padding: "1.5rem",
        maxWidth: "1400px",
        margin: "0 auto",
        backgroundColor: "#f9fafb",
        minHeight: "100vh",
      }}
    >
      {/* HEADER: Izq título | Centro tarjeta necesidades | Der botón */}
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "12px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.10)",
          padding: "1.25rem",
          marginBottom: "1rem",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr minmax(260px, 360px) 1fr",
            alignItems: "center",
            gap: "1rem",
          }}
        >
          <div>
            <h1
              style={{
                fontSize: "1.55rem",
                fontWeight: 900,
                color: "#111827",
                margin: "0 0 0.25rem 0",
              }}
            >
              💰 Dashboard Presupuestal
            </h1>
            <p style={{ color: "#6b7280", margin: 0, fontSize: "1rem" }}>
              Seguimiento de Necesidades, CDP y RP en tiempo real
            </p>
          </div>

          <div>
            <StatCard
              title="Necesidades Aprobadas"
              value={computed.necAprob}
              icon={<CheckCircle size={26} />}
              color={COLORS.success}
              bgColor="#d1fae5"
              compact
            />
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button
              onClick={() => {
                const role = localStorage.getItem("role") || "usuario";
                const url = apiConfig.getUrl(
                  "/api/export/presupuesto.php",
                  `role=${encodeURIComponent(role)}`
                );
                window.open(url, "_blank");
              }}
              style={{
                padding: "0.65rem 1rem",
                backgroundColor: "#10b981",
                color: "white",
                border: "none",
                borderRadius: "12px",
                fontSize: "1rem",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                fontWeight: 900,
              }}
            >
              <FileText size={18} />
              Descargar Excel
            </button>
          </div>
        </div>
      </div>

      {/* Línea superior de la "T" */}
      <div
        style={{
          height: "2px",
          backgroundColor: "#e5e7eb",
          margin: "1rem 0 1.25rem 0",
        }}
      />

      {/* CUERPO EN "T": CDP izquierda | RP derecha */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "2.5rem",
          alignItems: "start",
        }}
      >
        {/* CDP */}
        <div style={{ paddingLeft: "0.25rem" }}>
          <div
            style={{
              textAlign: "center",
              fontWeight: 900,
              color: "#111827",
              marginBottom: "0.75rem",
            }}
          >
            CDP
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(180px, 1fr))",
              gap: "1rem",
            }}
          >
            <MiniStatCard
              title="CDP Solicitados"
              value={computed.cdp.solicitados}
              color={COLORS.info}
            />
            <MiniStatCard
              title="CDP Aprobados"
              value={computed.cdp.aprobados}
              color={COLORS.success}
            />
            <MiniStatCard
              title="CDP Rechazados"
              value={computed.cdp.rechazados}
              color={COLORS.danger}
            />
            <MiniStatCard
              title="En proceso"
              value={computed.cdp.enProceso}
              color={COLORS.warning}
            />
          </div>
        </div>

        {/* RP */}
        <div style={{ paddingRight: "0.25rem" }}>
          <div
            style={{
              textAlign: "center",
              fontWeight: 900,
              color: "#111827",
              marginBottom: "0.75rem",
            }}
          >
            RP
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(180px, 1fr))",
              gap: "1rem",
            }}
          >
            <MiniStatCard
              title="RP Solicitados"
              value={computed.rp.solicitados}
              color={COLORS.info}
            />
            <MiniStatCard
              title="RP Aprobados"
              value={computed.rp.aprobados}
              color={COLORS.success}
            />
            <MiniStatCard
              title="Sin solicitud"
              value={computed.rp.sinSolicitud}
              color={COLORS.gray}
            />
            <MiniStatCard
              title="RP Rechazados"
              value={computed.rp.rechazados}
              color={COLORS.danger}
            />
            <MiniStatCard
              title="En proceso"
              value={computed.rp.enProceso}
              color={COLORS.warning}
            />
          </div>
        </div>
      </div>

      {/* Línea vertical de la "T" */}
      <div
        style={{
          width: "2px",
          height: "70px",
          backgroundColor: "#e5e7eb",
          margin: "0.75rem auto 0.75rem auto",
        }}
      />

      {/* CIERRE de la "T": Necesidades sin RP centrado */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginBottom: "1.5rem",
        }}
      >
        <div style={{ width: "min(720px, 100%)" }}>
          <StatCard
            title="Necesidades sin RP"
            value={computed.necesidadesSinRP}
            icon={<AlertCircle size={26} />}
            color={COLORS.danger}
            bgColor="#fee2e2"
            wide
          />
        </div>
      </div>

      {/* DISTRIBUCIÓN POR SECRETARÍA (ALFABÉTICO + TOTAL ARRIBA) */}
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "12px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.10)",
          padding: "1.25rem",
          marginBottom: "1.5rem",
        }}
      >
        <h3 style={{ fontSize: "1.2rem", fontWeight: 900, marginBottom: "0.75rem" }}>
          🏛️ Distribución por Secretaría
        </h3>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr
                style={{
                  backgroundColor: "#f3f4f6",
                  borderBottom: "2px solid #e5e7eb",
                }}
              >
                <th
                  style={{
                    padding: "0.75rem",
                    textAlign: "left",
                    fontWeight: 900,
                    fontSize: "0.9rem",
                  }}
                >
                  SECRETARÍA
                </th>
                <th
                  style={{
                    padding: "0.75rem",
                    textAlign: "center",
                    fontWeight: 900,
                    fontSize: "0.9rem",
                  }}
                >
                  NECESIDADES APROBADAS
                </th>
                <th
                  style={{
                    padding: "0.75rem",
                    textAlign: "center",
                    fontWeight: 900,
                    fontSize: "0.9rem",
                  }}
                >
                  CDP APROBADOS
                </th>
                {/* ✅ SIN CDP */}
                <th
                  style={{
                    padding: "0.75rem",
                    textAlign: "center",
                    fontWeight: 900,
                    fontSize: "0.9rem",
                  }}
                >
                  SIN CDP
                </th>
                <th
                  style={{
                    padding: "0.75rem",
                    textAlign: "center",
                    fontWeight: 900,
                    fontSize: "0.9rem",
                  }}
                >
                  RP APROBADOS
                </th>

                {/* ✅ NUEVA COLUMNA: % CUMPLIMIENTO RP */}
                <th
                  style={{
                    padding: "0.75rem",
                    textAlign: "center",
                    fontWeight: 900,
                    fontSize: "0.9rem",
                  }}
                >
                  % CUMPLIMIENTO RP
                </th>
              </tr>
            </thead>

            <tbody>
              {computed.distWithTotalFirst.map((sec, idx) => {
                const isTotal = !!sec.__isTotal;

                const nec = toNum(sec.necesidades_aprobadas);
                const cdp = toNum(sec.cdp_aprobados);
                const rp = toNum(sec.rp_aprobados);

                // ✅ Sin CDP = Necesidades aprobadas - CDP aprobados
                const sinCdp = clamp0(nec - cdp);

                // ✅ % cumplimiento RP = RP aprobados / necesidades aprobadas
                const cumplimientoRp = pct(rp, nec);

                return (
                  <tr
                    key={idx}
                    style={{
                      borderBottom: "1px solid #e5e7eb",
                      backgroundColor: isTotal ? "#f9fafb" : "transparent",
                      borderTop: isTotal ? "3px solid #d1d5db" : undefined,
                    }}
                  >
                    <td
                      style={{
                        padding: "0.75rem",
                        fontWeight: isTotal ? 900 : 600,
                        fontSize: "0.95rem",
                        color: "#111827",
                      }}
                    >
                      {sec.secretaria || "Sin secretaría"}
                    </td>

                    <td
                      style={{
                        padding: "0.75rem",
                        textAlign: "center",
                        fontSize: "1rem",
                        fontWeight: isTotal ? 900 : 800,
                        color: COLORS.success,
                      }}
                    >
                      {nec}
                    </td>

                    <td
                      style={{
                        padding: "0.75rem",
                        textAlign: "center",
                        fontSize: "1rem",
                        fontWeight: isTotal ? 900 : 800,
                        color: COLORS.info,
                      }}
                    >
                      {cdp}
                    </td>

                    {/* ✅ SIN CDP */}
                    <td
                      style={{
                        padding: "0.75rem",
                        textAlign: "center",
                        fontSize: "1rem",
                        fontWeight: isTotal ? 900 : 800,
                        color: COLORS.warning,
                      }}
                    >
                      {sinCdp}
                    </td>

                    <td
                      style={{
                        padding: "0.75rem",
                        textAlign: "center",
                        fontSize: "1rem",
                        fontWeight: isTotal ? 900 : 800,
                        color: COLORS.purple,
                      }}
                    >
                      {rp}
                    </td>

                    {/* ✅ % CUMPLIMIENTO RP */}
                    <td
                      style={{
                        padding: "0.75rem",
                        textAlign: "center",
                        fontSize: "1rem",
                        fontWeight: isTotal ? 900 : 800,
                        color: cumplimientoRp >= 80 ? COLORS.success : cumplimientoRp >= 50 ? COLORS.warning : COLORS.danger,
                      }}
                      title={`${rp} RP aprobados / ${nec} necesidades aprobadas`}
                    >
                      {fmtPct(cumplimientoRp)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-4 mb-5 text-center text-muted">
        <p style={{ color: "#9ca3af", fontSize: "0.9rem" }}>
          Dashboard Presupuestal | Municipio de Copacabana © 2026
        </p>
      </div>
    </div>
  );
};

/* =========================
   Componentes auxiliares
========================= */

const StatCard = ({ title, value, icon, color, bgColor, compact, wide }) => (
  <div
    style={{
      backgroundColor: "white",
      borderRadius: "12px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.10)",
      padding: compact ? "1rem 1.1rem" : wide ? "1.35rem 1.5rem" : "1.5rem",
    }}
  >
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
      <div>
        <p
          style={{
            fontSize: compact ? "0.9rem" : "0.95rem",
            color: "#6b7280",
            margin: "0 0 0.5rem 0",
            fontWeight: 900,
          }}
        >
          {title}
        </p>
        <p
          style={{
            fontSize: compact ? "2.0rem" : wide ? "2.6rem" : "2.2rem",
            fontWeight: 900,
            color,
            margin: 0,
          }}
        >
          {value}
        </p>
      </div>
      <div style={{ backgroundColor: bgColor, padding: "0.85rem", borderRadius: "12px", color }}>
        {icon}
      </div>
    </div>
  </div>
);

const MiniStatCard = ({ title, value, color }) => (
  <div
    style={{
      backgroundColor: "white",
      borderRadius: "12px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.10)",
      padding: "1.2rem",
      minHeight: "92px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    }}
  >
    <p style={{ fontSize: "0.85rem", color: "#6b7280", margin: "0 0 0.45rem 0", fontWeight: 900 }}>
      {title}
    </p>
    <p style={{ fontSize: "1.8rem", fontWeight: 900, color, margin: 0 }}>
      {value}
    </p>
  </div>
);

export default Presupuesto;
